import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Checkbox, Space, Card, Select, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;

type CommunicationType = 'CONTRACTS' | 'BOOKING_REQUESTS' | 'SHIPPING_INSTRUCTIONS' | 'SHIPPING_DOCS' | 'SHIPPING_REPORTS' | 'INVOICES' | 'FINANCIAL_REPORTS' | 'GENERAL';
type RecipientType = 'TO' | 'CC';

interface CommunicationPreference {
  communicationType: CommunicationType;
  recipientType: RecipientType;
  enabled: boolean;
}

interface Contact {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  isPrimary: boolean;
  communicationPreferences?: CommunicationPreference[];
}

interface Forwarder {
  id: string;
  company: string;
  active: boolean;
  contacts?: Contact[];
}

interface ForwarderManagementModalProps {
  visible: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  forwarder?: Forwarder | null;
}

const communicationTypes: { value: CommunicationType; label: string }[] = [
  { value: 'BOOKING_REQUESTS', label: 'Booking Requests' },
  { value: 'SHIPPING_INSTRUCTIONS', label: 'Shipping Instructions' },
  { value: 'SHIPPING_DOCS', label: 'Shipping Documents' },
  { value: 'SHIPPING_REPORTS', label: 'Shipping Reports' },
  { value: 'CONTRACTS', label: 'Contracts' },
  { value: 'INVOICES', label: 'Invoices' },
  { value: 'FINANCIAL_REPORTS', label: 'Financial Reports' },
  { value: 'GENERAL', label: 'General' },
];

export const ForwarderManagementModal: React.FC<ForwarderManagementModalProps> = ({
  visible,
  onClose,
  mode,
  forwarder
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      if (mode === 'edit' && forwarder) {
        const contacts = forwarder.contacts?.map(contact => ({
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.phone || '',
          title: contact.title || '',
          department: contact.department || '',
          isPrimary: contact.isPrimary,
          communicationPreferences: contact.communicationPreferences || [],
        })) || [];

        form.setFieldsValue({
          company: forwarder.company,
          active: forwarder.active,
          contacts: contacts.length > 0 ? contacts : [{
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            title: '',
            department: '',
            isPrimary: true,
            communicationPreferences: [],
          }],
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          active: true,
          contacts: [{
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            title: '',
            department: '',
            isPrimary: true,
            communicationPreferences: [],
          }],
        });
      }
    }
  }, [visible, mode, forwarder, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const payload = {
        company: values.company,
        active: values.active,
        contacts: values.contacts?.map((contact: any) => ({
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.phone || undefined,
          title: contact.title || undefined,
          department: contact.department || undefined,
          isPrimary: contact.isPrimary,
          communicationPreferences: contact.communicationPreferences || [],
        })),
      };

      if (mode === 'create') {
        await api.post('/forwarders', payload);
        message.success('Forwarder created successfully');
      } else {
        await api.put(`/forwarders/${forwarder?.id}`, payload);
        message.success('Forwarder updated successfully');
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving forwarder:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save forwarder';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={mode === 'create' ? 'Add Forwarder' : 'Edit Forwarder'}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {mode === 'create' ? 'Create' : 'Update'}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        {/* Company Name */}
        <Form.Item
          label="Company Name"
          name="company"
          rules={[{ required: true, message: 'Please enter company name' }]}
        >
          <Input placeholder="e.g., Castle Forwarding" />
        </Form.Item>

        {/* Active Status */}
        <Form.Item
          label="Status"
          name="active"
          valuePropName="checked"
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>

        {/* Contacts */}
        <Card title="Contacts" style={{ marginTop: 24 }}>
          <Form.List name="contacts">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Card
                    key={key}
                    type="inner"
                    title={`Contact ${index + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      ) : null
                    }
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      {/* Name Fields */}
                      <Space style={{ width: '100%' }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'firstName']}
                          rules={[{ required: true, message: 'Required' }]}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="First Name" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'lastName']}
                          rules={[{ required: true, message: 'Required' }]}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="Last Name" />
                        </Form.Item>
                      </Space>

                      {/* Email and Phone */}
                      <Space style={{ width: '100%' }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'email']}
                          rules={[
                            { required: true, message: 'Required' },
                            { type: 'email', message: 'Invalid email' }
                          ]}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="Email" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'phone']}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="Phone (optional)" />
                        </Form.Item>
                      </Space>

                      {/* Title and Department */}
                      <Space style={{ width: '100%' }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'title']}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="Title (optional)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'department']}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="Department (optional)" />
                        </Form.Item>
                      </Space>

                      {/* Primary Contact */}
                      <Form.Item
                        {...restField}
                        name={[name, 'isPrimary']}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox>Primary Contact</Checkbox>
                      </Form.Item>

                      {/* Communication Preferences */}
                      <div>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>Communication Preferences:</div>
                        <Form.List name={[name, 'communicationPreferences']}>
                          {(prefFields, { add: addPref, remove: removePref }) => (
                            <>
                              {prefFields.map((prefField) => (
                                <div key={prefField.key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                                  <Form.Item
                                    {...prefField}
                                    name={[prefField.name, 'communicationType']}
                                    style={{ marginBottom: 0, flex: 1 }}
                                  >
                                    <Select placeholder="Select type">
                                      {communicationTypes.map(type => (
                                        <Option key={type.value} value={type.value}>{type.label}</Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                  <Form.Item
                                    {...prefField}
                                    name={[prefField.name, 'recipientType']}
                                    style={{ marginBottom: 0, width: 80 }}
                                    initialValue="TO"
                                  >
                                    <Select>
                                      <Option value="TO">
                                        <Tag color="green">TO</Tag>
                                      </Option>
                                      <Option value="CC">
                                        <Tag color="blue">CC</Tag>
                                      </Option>
                                    </Select>
                                  </Form.Item>
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removePref(prefField.name)}
                                  />
                                </div>
                              ))}
                              <Button
                                type="dashed"
                                onClick={() => addPref({ communicationType: 'BOOKING_REQUESTS', recipientType: 'TO', enabled: true })}
                                block
                                icon={<PlusOutlined />}
                                size="small"
                              >
                                Add Communication Type
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </div>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    title: '',
                    department: '',
                    isPrimary: false,
                    communicationPreferences: [],
                  })}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Contact
                </Button>
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </Modal>
  );
};

export default ForwarderManagementModal;
